/**
 * The signed-in user's coin-based rank (specs/coin-based-ranks). Driven from the coin store,
 * NOT the XP summary: coin gains that award no XP (daily claim, coupon, purchase) never refetch
 * the XP summary, so reading rank from there would leave the badge stale and skip the rank-up
 * celebration. `peakCoins` is the server-hydrated all-time high; `max(peakCoins, coins)` self-heals
 * any drift and covers a payload that predates the field.
 */

import { useMemo } from 'react';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { getRankForPeakCoins } from '../helpers/rankRegistry.helper';
import type { RankDef } from '../constants/ranks.constants';

export interface SelfRank {
  /** RankDef for the badge, or null until a trusted balance has synced (no misleading Rookie). */
  rank: RankDef | null;
  /** Rank index 1..10, or null while unloaded. Used by the rank-up celebration watcher. */
  rankIndex: number | null;
  /** The effective peak coins the rank is derived from (max of stored peak and live balance). */
  effectivePeak: number;
}

export function useSelfRank(): SelfRank {
  const coins = useEconomyStore((s) => s.coins);
  const peakCoins = useEconomyStore((s) => s.peakCoins);
  const coinsLoaded = useEconomyStore((s) => s.coinsLoaded);

  return useMemo(() => {
    const effectivePeak = Math.max(peakCoins, coins);
    if (!coinsLoaded) {
      return { rank: null, rankIndex: null, effectivePeak };
    }
    const rank = getRankForPeakCoins(effectivePeak);
    return { rank, rankIndex: rank.index, effectivePeak };
  }, [coins, peakCoins, coinsLoaded]);
}
